import type { Todo } from "../../domain/entities.ts";
import { bindDragList } from "../../ui/drag-list.ts";
import { moveItem } from "./productivity.ts";

export interface TaskPositionAssignment { id: string; position: number }

export function taskPositionAssignments(ids: string[], tasks: Todo[]): TaskPositionAssignment[] {
  const ordered = ids.map(id => tasks.find(todo => todo.id === id)).filter((todo): todo is Todo => !!todo);
  const slots = ordered.map(todo => todo.position).sort((left, right) => left - right);
  return ordered.map((todo, index) => ({ id: todo.id, position: slots[index] })).filter(assignment => tasks.find(todo => todo.id === assignment.id)?.position !== assignment.position);
}

export interface TaskListReorderCapabilities<Snapshot, Field extends string> {
  enabled(): boolean; editable(): boolean; myDay(): boolean; tasks(): Todo[]; rootId(): string | null;
  snapshot(task: Todo): Snapshot;
  mutate(id: string, update: (task: Todo) => void): Promise<unknown>;
  moveMyDay(id: string, direction: -1 | 1): Promise<void>;
  reorderMyDay(ids: string[]): Promise<void>;
  render(full?: boolean): Promise<void>;
  undo(message: string, rootId: string, snapshots: Snapshot[], fields: Field[]): void;
  toast(message: string): void; sync(): void; hapticFeedback(): void;
}

export function createTaskListReorderController<Snapshot, Field extends string>(capabilities: TaskListReorderCapabilities<Snapshot, Field>) {
  const persistOrder = async (ids: string[], message: string, fullRender: boolean): Promise<void> => {
    const tasks = ids.map(id => capabilities.tasks().find(todo => todo.id === id)).filter((todo): todo is Todo => !!todo);
    const before = tasks.map(capabilities.snapshot); const assignments = taskPositionAssignments(ids, capabilities.tasks());
    await Promise.all(assignments.map(assignment => capabilities.mutate(assignment.id, todo => { todo.position = assignment.position; })));
    await capabilities.render(fullRender); const rootId = capabilities.rootId(); if (rootId) capabilities.undo(message, rootId, before, ["position"] as Field[]); capabilities.sync();
  };
  return {
    bind(root: ParentNode): void {
      if ((!capabilities.editable() && !capabilities.myDay()) || !capabilities.enabled()) return;
      root.querySelectorAll<HTMLElement>(".todo-list").forEach(list => bindDragList(list, ".todo-row", { hapticFeedback: capabilities.hapticFeedback, dropped: async () => {
        const ids = Array.from(list.querySelectorAll<HTMLElement>(":scope > .todo-row")).map(row => row.dataset.todo!);
        if (capabilities.myDay()) { await capabilities.reorderMyDay(ids); await capabilities.render(false); capabilities.toast("Today’s order saved"); return; }
        await persistOrder(ids, "Task order saved", false);
      }}));
    },
    async move(id: string, direction: -1 | 1): Promise<void> {
      if (!capabilities.enabled()) return;
      if (capabilities.myDay()) { await capabilities.moveMyDay(id, direction); await capabilities.render(false); document.querySelector<HTMLElement>(`[data-todo="${CSS.escape(id)}"]`)?.focus(); return; }
      const row = document.querySelector<HTMLElement>(`.todo-row[data-todo="${CSS.escape(id)}"]`); const list = row?.parentElement; if (!row || !list) return;
      const rows = Array.from(list.querySelectorAll<HTMLElement>(":scope > .todo-row")); const reordered = moveItem(rows, rows.indexOf(row), direction);
      if (reordered.every((item, index) => item === rows[index])) return;
      await persistOrder(reordered.map(item => item.dataset.todo!), `Moved task ${direction < 0 ? "up" : "down"}`, true);
    },
  };
}
